import React from "react";
import { Box, Flex, Heading, Spinner, Text } from "@chakra-ui/react";
import { Content } from "@prismicio/client";
import { SliceComponentProps } from "@prismicio/react";
import styles from "./ContactDetails.module.css";
import addLineBreaks from "@/functions/AddLineBreaks";
import GoogleMapComp from "@/components/GoogleMap/GoogleMap";
import useSettings from "@/hooks/useSettings";
import * as prismic from "@prismicio/client";

/**
 * Props for `ContactDetails`.
 */
export type ContactDetailsProps =
  SliceComponentProps<Content.ContactDetailsSlice>;

/**
 * Component for "ContactDetails" Slices.
 */
const ContactDetails = ({ slice }: ContactDetailsProps): JSX.Element => {
  const { settings, isLoading, error } = useSettings();
  const {
    address,
    email,
    phone_number,
    map_marker_location,
    map_info_window_title,
    map_info_window_description,
    link_to_google_maps,
  } = settings ?? {};

  const renderContent = () => {
    if (isLoading || error) {
      return (
        <Flex alignItems="center" justifyContent="center" width="100%">
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
            size="xl"
          />
        </Flex>
      );
    }

    return (
      <Box className={styles.contactDetails__wrapper}>
        <Box className={styles.contactContent}>
          <Box className={styles.contactDetails__contentInner}>
            <Heading
              textStyle="h2"
              as="h1"
              fontSize="40px"
              marginBottom="34px"
              letterSpacing="1px"
              color="brand.dark"
            >
              Contact Us
            </Heading>

            {address && (
              <>
                <Text
                  textStyle="p"
                  color="brand.dark"
                  fontWeight="bold"
                  maxWidth="100%"
                  marginBottom="0"
                >
                  Address
                </Text>
                <Text textStyle="p" color="brand.dark" maxWidth="100%">
                  {addLineBreaks(prismic.asText(address))}
                </Text>
              </>
            )}

            {email && (
              <>
                <Text
                  textStyle="p"
                  color="brand.dark"
                  fontWeight="bold"
                  maxWidth="100%"
                  marginBottom="0"
                >
                  Email
                </Text>
                <a
                  href={`mailto: ${prismic.asText(email)}`}
                  className="linkUnderline"
                >
                  <Text textStyle="p" color="brand.dark" maxWidth="100%">
                    {addLineBreaks(prismic.asText(email))}
                  </Text>
                </a>
              </>
            )}

            {phone_number && (
              <>
                <Text
                  textStyle="p"
                  color="brand.dark"
                  fontWeight="bold"
                  maxWidth="100%"
                  marginBottom="0"
                >
                  Phone
                </Text>
                <a
                  href={`tel: ${prismic.asText(phone_number)}`}
                  className="linkUnderline"
                >
                  <Text textStyle="p" color="brand.dark" maxWidth="100%">
                    {addLineBreaks(prismic.asText(phone_number))}
                  </Text>
                </a>
              </>
            )}
          </Box>
        </Box>
        <Flex
          alignItems="center"
          justifyContent="center"
          className={styles.contactMap}
          minHeight="500px"
        >
          {map_marker_location && (
            <GoogleMapComp
              infoWindowTitle={map_info_window_title ?? ""}
              infoWindowDescription={map_info_window_description ?? ""}
              googleMapsLink={link_to_google_maps ?? ""}
              geoCoordinates={{
                lat: map_marker_location.latitude,
                lng: map_marker_location.longitude,
              }}
            />
          )}
        </Flex>
      </Box>
    );
  };

  return (
    <Flex className={styles.contactWrapper} minHeight="500px">
      {renderContent()}
    </Flex>
  );
};

export default ContactDetails;
